"use client";

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, UserPlus } from 'lucide-react';
import { showSuccess, showError } from '@/utils/toast';
import { supabase } from '@/utils/supabase/client';

interface InviteFormProps {
  companyId: string; // Pass from auth context
}

export const InviteForm = ({ companyId }: InviteFormProps) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'client' | 'agent' | 'admin'>('client');
  const [loading, setLoading] = useState(false);

  const handleInvite = async () => {
    if (!email) {
      showError('Email is required');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-invite', {
        body: { email, company_id: companyId, role },
      });

      if (error) {
        showError(error.message || 'Failed to send invite');
      } else if (data?.success) {
        showSuccess(`Invite sent to ${email}!`);
        setEmail('');
      } else {
        showError(data?.error || 'Failed to send invite');
      }
    } catch (error: any) {
      console.error('Invite error:', error);
      showError('Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <UserPlus className="h-4 w-4" />
          <span>Invite Team Member</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="role">Role</Label>
          <select id="role" value={role} onChange={(e) => setRole(e.target.value as 'client' | 'agent' | 'admin')}>
            <option value="client">Client</option>
            <option value="agent">Agent</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <Button onClick={handleInvite} disabled={loading || !email} className="w-full flex items-center space-x-2">
          <Mail className="h-4 w-4" />
          {loading ? 'Sending...' : 'Send Invite'}
        </Button>
      </CardContent>
    </Card>
  );
};